import classNames from 'classnames';
import CurrencyFormat from 'components/CurrencyFormat';
import DurationFormat from 'components/DurationFormat';
import React, { useMemo } from 'react';
import type { DataItemBillingSummary } from 'redux/modules/superreports.types';
import type { Currency } from 'types/account';
import styles from './WidgetTooltip.module.scss';

export type ValueFormatter = (value: number) => React.ReactNode;

const defaultValueFormatter: ValueFormatter = value => <span>{value}</span>;

interface HoursValueProps {
  value: number;
}

const HoursValue: React.FC<HoursValueProps> = ({ value }) => (
  <DurationFormat value={value} valueType="hours" format="h[h] mm[m]" />
);

export const hoursValueFormatter = (value: number) => <HoursValue value={value} />;

interface MoneyValueProps {
  value: number;
  currency: Currency;
}

const MoneyValue: React.FC<MoneyValueProps> = ({ value, currency }) => (
  <CurrencyFormat value={value} currency={currency} animated={false} />
);

export const moneyValueFormatterFactory = (currency: Currency) => {
  return function MoneyValueFormatter(value: number) {
    return <MoneyValue value={value} currency={currency} />;
  };
};

interface Props {
  title: string;
  subTitle?: string;
  value: number;
  valueType?: 'hours' | 'money';
  currency?: Currency;
  billingSummary?: DataItemBillingSummary;
  percentOfTotal?: number | null;
}

const WidgetTooltip: React.FC<Props> = props => {
  const { title, subTitle, value, valueType, currency, billingSummary, percentOfTotal } = props;

  const valueFormatter = useMemo(() => {
    if (valueType === 'money') {
      if (!currency) {
        return defaultValueFormatter;
      }
      return moneyValueFormatterFactory(currency);
    } else if (valueType === 'hours') {
      return hoursValueFormatter;
    } else {
      return defaultValueFormatter;
    }
  }, [currency, valueType]);

  return (
    <div className={styles.tooltipContainer}>
      <div className={styles.header}>
        {title}
        {subTitle && <span>({subTitle})</span>}
      </div>
      {percentOfTotal != null && (
        <div className={styles.percentOfTotal}>{percentOfTotal.toFixed(1)}%</div>
      )}
      {billingSummary?.billedAmount != null && (
        <>
          <span className={styles.label}>Billed</span>
          <span className={styles.percent}>{billingSummary.billedPercent?.toFixed(0) ?? '0'}%</span>
          {valueFormatter(billingSummary.billedAmount ?? 0)}
        </>
      )}
      {billingSummary?.unbilledAmount != null && (
        <>
          <span className={styles.label}>Unbilled</span>
          <span className={styles.percent}>
            {billingSummary.unbilledPercent?.toFixed(0) ?? '0'}%
          </span>
          {valueFormatter(billingSummary.unbilledAmount ?? 0)}
        </>
      )}
      {billingSummary?.billableAmount != null && (
        <>
          <span className={styles.label}>Billable</span>
          <span className={styles.percent}>
            {billingSummary.billablePercent?.toFixed(0) ?? '0'}%
          </span>
          {valueFormatter(billingSummary.billableAmount ?? 0)}
        </>
      )}
      {billingSummary?.nonBillableAmount != null && (
        <>
          <span className={styles.label}>Non-billable</span>
          <span className={styles.percent}>
            {billingSummary.nonBillablePercent?.toFixed(0) ?? '0'}%
          </span>
          {valueFormatter(billingSummary.nonBillableAmount ?? 0)}
        </>
      )}
      {billingSummary && <div className={styles.divider} />}
      <span className={classNames(styles.total, styles.label)}>Total</span>
      {valueFormatter(value)}
    </div>
  );
};

export default WidgetTooltip;
